// sellerOrderDetail.js — /seller/orders: the payment-type cell, the tracking
// column and the inline order detail under a row (owner 2026-10-15: "ประเภท
// การชำระเงินไม่ขึ้น (ชำระผ่าน 2C2P) แบบบัตรเครดิต · เพิ่มการแสดงหมายเลข
// Tracking Number · กดที่เลข Order เพื่อ drill down ... ยกเลิกการแสดงแบบ Modal").
//
// Pure helpers only (no vue import): tests/unit/sellerOrderDetail.spec.js
// drives them directly. Nothing here throws.

import { cdnImageUrl } from './cdnImage';

/**
 * mdfpf_orders.payment_method_type of a 2C2P card payment (bo-api
 * controllers/const.go PaymentMethodTypeCreditCard = 3). The shared store list
 * (payment_method_module.js) has id 3 commented out because the BO never
 * creates such orders — but every App card payment carries it, so the seller
 * page names it itself.
 */
export const PAYMENT_TYPE_CREDIT_CARD = 3;

/** i18n key of the 2C2P card label. */
export const CARD_LABEL_KEY = 'seller_orders.pay_type_card_2c2p';

function toInt(v) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
}

function trim(v) {
  return v === null || v === undefined ? '' : String(v).trim();
}

/**
 * sellerPaymentTypeLabel(id, storeList, t, fallbackName) — the ประเภทชำระเงิน
 * text of one order.
 *  - 3 (2C2P card) → t(CARD_LABEL_KEY)
 *  - an id the store list names → that name
 *  - otherwise the order's own payment_method_name when it has one
 *  - otherwise "-" (0 / unknown never reads as "all")
 */
export function sellerPaymentTypeLabel(id, storeList, t, fallbackName) {
  const n = toInt(id);
  if (n === PAYMENT_TYPE_CREDIT_CARD) {
    const label = typeof t === 'function' ? t(CARD_LABEL_KEY) : '';
    return label && label !== CARD_LABEL_KEY ? String(label) : 'Credit/Debit Card (2C2P)';
  }
  if (n > 0) {
    const list = Array.isArray(storeList) ? storeList : [];
    const hit = list.find(o => o && toInt(o.id) === n);
    if (hit && trim(hit.name)) return trim(hit.name);
  }
  const own = trim(fallbackName);
  return n > 0 && own ? own : '-';
}

/**
 * sellerPaymentChannelLabel(src, locale) — how a 2C2P order was REALLY paid
 * (bo-api Seller_payment_channel.go, read from 2C2P's notice): a list row's
 * `payment_channel_label_th/_en`, or a detail `payment.channel`'s
 * `label_th/_en`. '' when the server does not know (COD, unpaid, older API) —
 * the caller then keeps sellerPaymentTypeLabel.
 */
export function sellerPaymentChannelLabel(src, locale) {
  if (!src || typeof src !== 'object') return '';
  const en = locale === 'en';
  const label = en
    ? src.payment_channel_label_en || src.label_en || src.payment_channel_label_th || src.label_th
    : src.payment_channel_label_th || src.label_th || src.payment_channel_label_en || src.label_en;
  return trim(label);
}

/**
 * sellerPaymentChannelRows(channel) — the extra lines of the payment block for
 * a detail `payment.channel`: [{ key, value }] with i18n keys under
 * seller_orders.*; empty values are dropped. [] for no channel.
 */
export function sellerPaymentChannelRows(channel) {
  if (!channel || typeof channel !== 'object') return [];
  const card = [trim(channel.card_type), channel.card_last4 ? `•••• ${trim(channel.card_last4)}` : '']
    .filter(Boolean)
    .join(' ');
  const issuer = [trim(channel.issuer_bank), trim(channel.issuer_country)].filter(Boolean).join(' · ');
  const codes = [trim(channel.channel_code), trim(channel.agent_code), trim(channel.payment_scheme)]
    .filter(Boolean)
    .join(' / ');
  const result = [trim(channel.resp_code), trim(channel.resp_desc)].filter(Boolean).join(' ');
  const rows = [
    { key: 'seller_orders.pay_ch_card', value: card },
    { key: 'seller_orders.pay_ch_issuer', value: issuer },
    { key: 'seller_orders.pay_ch_approval', value: trim(channel.approval_code) },
    { key: 'seller_orders.pay_ch_tran_ref', value: trim(channel.tran_ref) },
    { key: 'seller_orders.pay_ch_reference', value: trim(channel.reference_no) },
    { key: 'seller_orders.pay_ch_time', value: formatBangkok(channel.transaction_time) },
    { key: 'seller_orders.pay_ch_result', value: result },
    { key: 'seller_orders.pay_ch_codes', value: codes },
  ];
  return rows.filter(r => r.value && r.value !== '-');
}

/**
 * trackingNumbersOf(src) — every tracking number of an order (list row or
 * detail response): `tracking_numbers` (bo-api 2026-10-15: App number + each
 * BO package) when it is a non-empty array, else `shipping_tracking_number`.
 * Trimmed, blanks and repeats dropped; [] when there is none.
 */
export function trackingNumbersOf(src) {
  const o = src && typeof src === 'object' ? src : {};
  const raw = Array.isArray(o.tracking_numbers) && o.tracking_numbers.length
    ? o.tracking_numbers
    : [o.shipping_tracking_number];
  const out = [];
  raw.forEach(v => {
    const s = trim(v);
    if (s && out.indexOf(s) < 0) out.push(s);
  });
  return out;
}

/**
 * sellerAddressText(order) — the recipient address line: fulladdress, else
 * detail/district/city/province; the postcode appended once (it is not part
 * of fulladdress); "-" when there is nothing.
 */
export function sellerAddressText(order) {
  const o = order && typeof order === 'object' ? order : {};
  const zip = trim(o.recipient_zipcode);
  let base = trim(o.recipient_address_fulladdress);
  if (!base) {
    base = [o.recipient_address_detail, o.recipient_address_district, o.recipient_address_city, o.recipient_address_province]
      .map(trim)
      .filter(Boolean)
      .join(' ');
  }
  const out = [base, zip].filter(Boolean).join(' ');
  return out || '-';
}

const pad = n => String(n).padStart(2, '0');

/**
 * formatBangkok(value) — "YYYY-MM-DD HH:mm" in Asia/Bangkok (+07, no DST).
 * Accepts epoch seconds (tracking_events.ts) or the API's naive Bangkok
 * "YYYY-MM-DDTHH:mm:ss" strings (shown as written). "" when empty/invalid.
 */
export function formatBangkok(value) {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value <= 0) return '';
    const d = new Date((value + 7 * 3600) * 1000);
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
  }
  const m = String(value).trim().match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  return m ? `${m[1]} ${m[2]}` : '';
}

/**
 * sellerTimeline(order, events) — the status timeline of the panel, oldest
 * first: order placed, paid, each carrier scan (tracking_events), cancelled.
 * Each entry: { key, labelKey?, text?, place?, at } — labelKey for the order's
 * own milestones (i18n), text for a carrier scan (the carrier's own words).
 * Entries without a time are dropped (nothing to place them by).
 */
export function sellerTimeline(order, events) {
  const o = order && typeof order === 'object' ? order : {};
  const out = [];
  const push = (entry, sortKey) => {
    if (entry.at) out.push(Object.assign(entry, { sortKey }));
  };
  const naiveKey = s => {
    const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
    return m ? Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0)) / 1000 - 7 * 3600 : 0;
  };
  push({ key: 'created', labelKey: 'seller_orders.tl_created', at: formatBangkok(o.created_at) }, naiveKey(o.created_at));
  push({ key: 'paid', labelKey: 'seller_orders.tl_paid', at: formatBangkok(o.payment_at) }, naiveKey(o.payment_at));
  (Array.isArray(events) ? events : []).forEach((e, i) => {
    if (!e || typeof e !== 'object') return;
    const ts = toInt(e.ts);
    const place = [trim(e.location), trim(e.postcode)].filter(Boolean).join(' ');
    push({ key: 'scan' + i, text: trim(e.status) || trim(e.status_code) || '-', place, at: formatBangkok(ts) }, ts);
  });
  push({ key: 'cancelled', labelKey: 'seller_orders.tl_cancelled', at: formatBangkok(o.cancelled_at) }, naiveKey(o.cancelled_at));
  return out
    .map((e, i) => ({ e, i }))
    .sort((a, b) => a.e.sortKey - b.e.sortKey || a.i - b.i)
    .map(({ e }) => {
      const copy = Object.assign({}, e);
      delete copy.sortKey;
      return copy;
    });
}

/** The panel's item lines from the detail response ([] when absent). */
export function sellerItemsOf(detail) {
  return detail && Array.isArray(detail.items) ? detail.items : [];
}

/** The panel's WMS lines from the detail response ([] when absent). */
export function sellerWmsItemsOf(detail) {
  return detail && Array.isArray(detail.wms_items) ? detail.wms_items : [];
}

/**
 * itemImageCandidates(item) — every picture the App order line can show, best
 * first, each a renderable URL (cdnImageUrl: absolute URLs byte for byte,
 * bare keys dropped — a browser cannot sign them). Same order as the customer
 * order pages (mdf-member-fn src/lib/orderLine.js orderLineImage):
 *  - a bundle line (selections[] non-empty): first selection picture → SKU →
 *    product
 *  - any other line: the bought SKU's own image (sku_image) → product main
 *    image (product_image) → a selection picture
 * bo-api heals a blank sku_image / product_image snapshot from the catalog
 * (owner 2026-10-15: order 261014465372D0 had both blank). The panel walks
 * this list when an <img> fails, so a dead SKU URL still falls back to the
 * product picture before the placeholder. [] when there is none.
 */
export function itemImageCandidates(item) {
  const it = item && typeof item === 'object' ? item : {};
  const sels = Array.isArray(it.selections) ? it.selections : [];
  const selImgs = sels.map(s => cdnImageUrl(s && s.image_url)).filter(Boolean);
  const sku = cdnImageUrl(it.sku_image);
  const product = cdnImageUrl(it.product_image);
  const ordered = sels.length ? selImgs.slice(0, 1).concat([sku, product], selImgs.slice(1)) : [sku, product].concat(selImgs);
  const out = [];
  ordered.forEach(u => {
    if (u && out.indexOf(u) < 0) out.push(u);
  });
  return out;
}

/** itemImage(item) — the line's best picture (itemImageCandidates[0]); "" when none. */
export function itemImage(item) {
  return itemImageCandidates(item)[0] || '';
}

export default {
  PAYMENT_TYPE_CREDIT_CARD,
  CARD_LABEL_KEY,
  sellerPaymentTypeLabel,
  trackingNumbersOf,
  sellerAddressText,
  formatBangkok,
  sellerTimeline,
  sellerItemsOf,
  sellerWmsItemsOf,
  itemImageCandidates,
  itemImage
};
